import { useLanguage } from '@/contexts/LanguageContext';

const tagsZh = [
  { icon: 'ri-microscope-line', label: '奈米級高分子設計' },
  { icon: 'ri-links-line', label: '擴鏈增黏' },
  { icon: 'ri-shield-check-line', label: '相容補強' },
  { icon: 'ri-test-tube-line', label: '低添加量 500–1000 ppm' },
  { icon: 'ri-recycle-line', label: 'Plastics Reviving' },
];

const tagsEn = [
  { icon: 'ri-microscope-line', label: 'Nano-Scale Polymer Design' },
  { icon: 'ri-links-line', label: 'Chain Extension & Viscosity Building' },
  { icon: 'ri-shield-check-line', label: 'Compatibility Enhancement' },
  { icon: 'ri-test-tube-line', label: 'Low Dosage 500–1000 ppm' },
  { icon: 'ri-recycle-line', label: 'Plastics Reviving' },
];

const tagsJa = [
  { icon: 'ri-microscope-line', label: 'ナノスケール高分子設計' },
  { icon: 'ri-links-line', label: '鎖延長・増粘' },
  { icon: 'ri-shield-check-line', label: '相容性強化' },
  { icon: 'ri-test-tube-line', label: '低添加量 500〜1000 ppm' },
  { icon: 'ri-recycle-line', label: 'Plastics Reviving' },
];

export default function TechHero() {
  const { lang } = useLanguage();
  const isEn = lang === 'en';
  const isJa = lang === 'ja';
  const tags = isEn ? tagsEn : isJa ? tagsJa : tagsZh;

  const sectionLabel = isEn ? 'Core Technology' : isJa ? 'コア技術' : '核心技術';

  const h1 = isEn ? (
    <>PQernel® Nano Chain Extension Technology —<br />
      Redefining the{' '}
      <span style={{ background: 'linear-gradient(90deg,#00B894 0%,#4FC3F7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        Material Value of Waste Plastics
      </span>
    </>
  ) : isJa ? (
    <>PQernel® ナノ鎖延長補強技術 —<br />
      廃プラスチックの
      <span style={{ background: 'linear-gradient(90deg,#00B894 0%,#4FC3F7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        材料価値を再定義する
      </span>
    </>
  ) : (
    <>PQernel® 奈米擴鏈補強技術，<br />
      重新定義廢棄塑膠的
      <span style={{ background: 'linear-gradient(90deg,#00B894 0%,#4FC3F7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
        材料價值
      </span>
    </>
  );

  const subtitle = isEn
    ? 'PQernel® is a proprietary nano-scale polymer chain extender developed by PolyQ. It helps recycled plastics rebuild molecular chain structures, improve material stability, and enhance processability during reprocessing — so waste plastics are not just recycled, but truly upgraded.'
    : isJa
    ? 'PQernel®は聚量応用材料が独自開発したナノスケール高分子鎖延長補強剤です。再生プロセスにおいて分子鎖構造の再構築・材料安定性の向上・加工性の改善を支援し、廃プラスチックを単なるリサイクルではなく、真のアップグレード再生へと導きます。'
    : 'PQernel® 是聚量自主開發的奈米級高分子擴鏈補強劑，能協助回收塑料在再製過程中補強分子鏈結構、提升材料穩定度、改善加工性，讓廢棄塑膠不只是回收，而是升級再生。';

  return (
    <section className="relative min-h-[580px] md:min-h-[660px] flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-top" style={{ backgroundImage: 'url(https://readdy.ai/api/search-image?query=nano%20polymer%20molecular%20structure%20visualization%20with%20glowing%20chain%20links%20green%20and%20blue%20technology%20abstract%20background%20material%20science%20laboratory%20recycled%20plastic%20granules%20professional%20industrial%20photography%20high%20resolution%20wide%20angle%20dark%20background%20with%20light%20particles&width=1440&height=660&seq=tech-hero-v1&orientation=landscape)' }} />
      <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/30 to-black/15" />
      <div className="absolute top-0 right-0 w-[650px] h-[650px] opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(0,184,148,0.32) 0%, transparent 65%)', zIndex: 2 }} aria-hidden="true" />
      <div className="absolute bottom-0 left-1/4 w-[450px] h-[350px] opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(2,115,194,0.28) 0%, transparent 65%)', zIndex: 2 }} aria-hidden="true" />
      <div className="relative w-full max-w-screen-xl mx-auto px-4 md:px-6 py-20" style={{ zIndex: 3 }}>
        <div className="max-w-[720px] bg-black/20 backdrop-blur-[2px] rounded-2xl px-8 py-8 md:px-10 md:py-10">
          <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-4">{sectionLabel}</p>
          <h1 className="font-bold text-white mb-6" style={{ fontSize: 'clamp(2rem, 4.2vw, 2.75rem)', lineHeight: '1.3' }}>{h1}</h1>
          <p className="text-white/85 mb-9 max-w-[640px]" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>{subtitle}</p>
          <div className="flex flex-wrap gap-2.5">
            {tags.map((tag) => (
              <div key={tag.label} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/25 backdrop-blur-sm">
                <i className={`${tag.icon} text-brand-green text-sm`}></i>
                <span className="text-white/90 text-[0.9375rem] font-medium whitespace-nowrap">{tag.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
